import React, { useState } from "react"

import {
  FormField,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"
import { NumberInput } from "@coval/ui/number-input"

import type { MetricRecordData } from "@/types/metrics/contracts"

import { normalizeLegacyPresetValue } from "../utils/presetVocabulary"

interface LoopDetectionConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

type LoopPresetKey = "strict" | "normal" | "lenient" | "custom"

type LoopNumberKey =
  | "min_loop_duration_seconds"
  | "min_repetition_count"
  | "loop_acoustic_similarity_threshold"
  | "display_severity_threshold"

const CUSTOM_OVERRIDE_KEYS = [
  "min_loop_duration_seconds",
  "min_repetition_count",
  "loop_acoustic_similarity_threshold",
] as const

const PRESET_OPTIONS: Array<{
  key: LoopPresetKey
  label: string
  description: string
}> = [
  {
    key: "strict",
    label: "Strict",
    description: "Catches more loops (0.6s, 2 reps)",
  },
  {
    key: "normal",
    label: "Normal",
    description: "Balanced sensitivity (1.0s, 3 reps)",
  },
  {
    key: "lenient",
    label: "Lenient",
    description: "Only flags clearer loops (1.5s, 4 reps)",
  },
  {
    key: "custom",
    label: "Custom",
    description: "Define your own thresholds",
  },
]

export const LoopDetectionConfig: React.FC<LoopDetectionConfigProps> = ({
  metricRecordData,
  onChange,
}) => {
  const metadata = metricRecordData.metric_metadata
  const storedPreset = normalizeLegacyPresetValue(
    metadata?.loop_detection_preset,
  )
  const hasCustomOverrideAtMount = CUSTOM_OVERRIDE_KEYS.some(
    (key) => metadata?.[key] !== undefined && metadata?.[key] !== null,
  )
  const [currentPreset, setCurrentPreset] = useState<string>(
    hasCustomOverrideAtMount ? "custom" : (storedPreset ?? "normal"),
  )
  const isCustom = currentPreset === "custom"
  // A stored preset this form does not offer is shown as it is, and kept until
  // the user picks another one.
  const isUnknownPreset = !PRESET_OPTIONS.some(
    (option) => option.key === currentPreset,
  )

  const handlePresetChange = (value: string) => {
    const selectedKey = PRESET_OPTIONS.find(
      (option) => option.key === value,
    )?.key
    if (!selectedKey) {
      return
    }

    setCurrentPreset(selectedKey)

    const nextMetadata = {
      ...metadata,
      loop_detection_preset: selectedKey === "custom" ? undefined : selectedKey,
    }
    if (selectedKey !== "custom") {
      nextMetadata.min_loop_duration_seconds = undefined
      nextMetadata.min_repetition_count = undefined
      nextMetadata.loop_acoustic_similarity_threshold = undefined
    }

    onChange({
      ...metricRecordData,
      metric_metadata: nextMetadata,
    })
  }

  const updateNumber = (key: LoopNumberKey, value: number | undefined) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metadata,
        [key]: value,
      },
    })
  }

  const numberField = ({
    key,
    label,
    tooltip,
    placeholder,
    min,
    max,
    step,
    className,
  }: {
    key: LoopNumberKey
    label: string
    tooltip: string
    placeholder: string
    min?: number
    max?: number
    step: number
    className?: string
  }) => (
    <FormField label={label} tooltip={tooltip} className={className}>
      {(ids) => (
        <NumberInput
          {...ids}
          value={metadata?.[key]}
          onValueChange={(value) => updateNumber(key, value)}
          min={min}
          max={max}
          step={step}
          placeholder={placeholder}
          incrementLabel={`Increase ${label}`}
          decrementLabel={`Decrease ${label}`}
        />
      )}
    </FormField>
  )

  return (
    <div className="space-y-4">
      <FormField
        label="Detection Sensitivity"
        description="Choose Custom to override individual thresholds."
      >
        {(ids) => (
          <Select value={currentPreset} onValueChange={handlePresetChange}>
            <SelectTrigger {...ids}>
              <SelectValue placeholder="Select sensitivity" />
            </SelectTrigger>
            <SelectContent>
              {PRESET_OPTIONS.map((option) => (
                <SelectItem
                  key={option.key}
                  value={option.key}
                  description={option.description}
                >
                  {option.label}
                </SelectItem>
              ))}
              {isUnknownPreset && (
                <SelectItem value={currentPreset} disabled>
                  {currentPreset}
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        )}
      </FormField>

      {isCustom && (
        <div className="grid grid-cols-2 gap-3">
          {numberField({
            key: "min_loop_duration_seconds",
            label: "Min Duration (s)",
            tooltip:
              "Shortest agent utterance the detector will consider as a loop candidate.",
            placeholder: "1.0",
            min: 0.1,
            step: 0.1,
          })}
          {numberField({
            key: "min_repetition_count",
            label: "Min Repetitions",
            tooltip:
              "A loop must repeat at least this many times to be flagged.",
            placeholder: "3",
            min: 2,
            step: 1,
          })}
          {numberField({
            key: "loop_acoustic_similarity_threshold",
            label: "Acoustic Similarity Threshold (0–1)",
            tooltip:
              "DTW similarity above which two utterances cluster as the same loop. Higher = stricter. Default 0.50.",
            placeholder: "0.50",
            min: 0,
            max: 1,
            step: 0.05,
            className: "col-span-2",
          })}
        </div>
      )}

      {numberField({
        key: "display_severity_threshold",
        label: "Display Severity Threshold (0–1)",
        tooltip:
          "Hide loops with per-repetition severity below this value from the deep-dive list and the audio waveform highlights. Doesn't affect what the backend detects.",
        placeholder: "0.4",
        min: 0,
        max: 1,
        step: 0.05,
      })}
    </div>
  )
}
